import { useState, useEffect } from 'react';
import { useAuth } from '@/providers/AuthProvider';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { ArrowLeft, HelpCircle, TrendingUp } from 'lucide-react';

interface RideDriverEarningsProps {
  onBack: () => void;
}

const RideDriverEarnings = ({ onBack }: RideDriverEarningsProps) => {
  const { user } = useAuth();
  const [weeklyEarnings, setWeeklyEarnings] = useState(0);
  const [tripCount, setTripCount] = useState(0);
  const [dailyBreakdown, setDailyBreakdown] = useState<{ day: string; amount: number; label: string }[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    const fetchEarnings = async () => {
      setLoading(true);
      
      // Get start of current week (Monday)
      const now = new Date();
      const dayOfWeek = now.getDay();
      const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
      const weekStart = new Date(now);
      weekStart.setDate(now.getDate() + mondayOffset);
      weekStart.setHours(0, 0, 0, 0);

      const { data: rides } = await supabase
        .from('ride_requests')
        .select('final_fare, estimated_fare, completed_at, created_at')
        .eq('driver_id', user.id)
        .eq('status', 'completed')
        .gte('completed_at', weekStart.toISOString());

      const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
      const daily: number[] = [0, 0, 0, 0, 0, 0, 0];
      let total = 0;

      if (rides && rides.length > 0) {
        rides.forEach(r => {
          const fare = r.final_fare || r.estimated_fare || 0;
          total += fare;
          const completedDate = new Date(r.completed_at || r.created_at);
          const day = completedDate.getDay();
          const idx = day === 0 ? 6 : day - 1;
          daily[idx] += fare;
        });
        setTripCount(rides.length);
      } else {
        setTripCount(0);
      }

      setWeeklyEarnings(total);
      setDailyBreakdown(daily.map((amount, i) => ({
        day: dayNames[i],
        amount,
        label: dayNames[i],
      })));
      setLoading(false);
    };
    fetchEarnings();
  }, [user]);

  const maxDaily = Math.max(...dailyBreakdown.map(d => d.amount), 1);

  const now = new Date();
  const dayOfWeek = now.getDay();
  const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  const weekStart = new Date(now);
  weekStart.setDate(now.getDate() + mondayOffset);
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);
  const fmt = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  // Current day index (Mon=0)
  const todayIdx = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

  return (
    <div className="min-h-screen bg-white">
      <div className="px-5 pt-6 pb-4">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={onBack}><ArrowLeft className="w-5 h-5" /></Button>
            <h2 className="text-2xl font-bold">Earnings</h2>
          </div>
          <HelpCircle className="w-6 h-6 text-muted-foreground" />
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-foreground" />
          </div>
        ) : (
          <>
            {/* Week range */}
            <p className="text-sm text-muted-foreground">{fmt(weekStart)} – {fmt(weekEnd)}</p>
            
            {/* Total earnings */}
            <div className="flex items-end gap-2 mt-1 mb-6">
              <p className="text-4xl font-bold">D{weeklyEarnings.toFixed(0)}</p>
              {weeklyEarnings > 0 && <TrendingUp className="w-5 h-5 text-green-500 mb-1.5" />}
            </div>

            {/* Bar chart */}
            <div className="bg-muted/30 rounded-2xl p-4 mb-6">
              <div className="flex items-end gap-2 h-32">
                {dailyBreakdown.map((bar, i) => {
                  const heightPct = maxDaily > 0 ? Math.max(4, (bar.amount / maxDaily) * 100) : 4;
                  const isToday = i === todayIdx;
                  return (
                    <div key={i} className="flex-1 flex flex-col items-center gap-1.5">
                      {bar.amount > 0 && (
                        <span className="text-[10px] font-medium text-muted-foreground">
                          D{bar.amount.toFixed(0)}
                        </span>
                      )}
                      <div className="w-full flex justify-center">
                        <div
                          className={`w-full max-w-[32px] rounded-t-lg transition-all duration-500 ${
                            isToday ? 'bg-blue-600' : bar.amount > 0 ? 'bg-blue-400' : 'bg-muted'
                          }`}
                          style={{ height: `${heightPct}%`, minHeight: '4px' }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex gap-2 mt-2">
                {dailyBreakdown.map((bar, i) => (
                  <div key={i} className={`flex-1 text-center text-xs ${
                    i === todayIdx ? 'font-bold text-foreground' : 'text-muted-foreground'
                  }`}>
                    {bar.label}
                  </div>
                ))}
              </div>
            </div>

            {/* Summary stats */}
            <div className="space-y-0">
              <div className="flex items-center justify-between py-4 border-t border-border">
                <span className="text-muted-foreground">Trips this week</span>
                <span className="font-bold text-lg">{tripCount}</span>
              </div>
              <div className="flex items-center justify-between py-4 border-t border-border">
                <span className="text-muted-foreground">Avg per trip</span>
                <span className="font-bold text-lg">
                  D{tripCount > 0 ? (weeklyEarnings / tripCount).toFixed(0) : '0'}
                </span>
              </div>
              <div className="flex items-center justify-between py-4 border-t border-b border-border">
                <span className="text-muted-foreground">Best day</span>
                <span className="font-bold text-lg">
                  {dailyBreakdown.length > 0 
                    ? `D${Math.max(...dailyBreakdown.map(d => d.amount)).toFixed(0)}`
                    : 'D0'
                  }
                </span>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default RideDriverEarnings;
